import { Edit as EditIcon } from '@mui/icons-material';
import { Box, Button, Chip, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../api/apiClient';
import { MarkdownView } from '../../components/MarkdownView';

interface BlogDetail {
  blogId: number;
  title: string;
  imageUrl: string | null;
  content: string;
  datePosted: string | null;
  createdAt: string;
  updatedAt: string;
  tags: string[];
}

export function BlogDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<BlogDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get(`/api/blogs/${id}`)
      .then(r => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then((data: BlogDetail) => setBlog(data))
      .catch(() => setError('Article not found'))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <Typography color='text.secondary'>Loading…</Typography>;
  if (error || !blog) return (
    <Box>
      <Typography color='error' sx={{ mb: 2 }}>{error ?? 'Article not found'}</Typography>
      <Button onClick={() => navigate('/blog')}>Back to Articles</Button>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 800 }}>
      <Stack direction='row' alignItems='center' justifyContent='space-between' sx={{ mb: 2 }}>
        <Button variant='text' onClick={() => navigate('/blog')} sx={{ pl: 0 }}>← Back</Button>
        <IconButton onClick={() => navigate(`/blog/${blog.blogId}/edit`)} title='Edit post'>
          <EditIcon />
        </IconButton>
      </Stack>

      {blog.imageUrl && (
        <Box component='img' src={blog.imageUrl} alt={blog.title}
          sx={{ width: '100%', maxHeight: 380, objectFit: 'cover', borderRadius: 2, mb: 3 }} />
      )}

      <Typography variant='h3' sx={{ fontWeight: 700, mb: 1 }}>{blog.title}</Typography>

      <Stack direction='row' spacing={2} alignItems='center' sx={{ mb: 1 }}>
        <Typography variant='caption' color='text.secondary'>
          {new Date(blog.datePosted ? blog.datePosted + 'T00:00:00' : blog.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
        {blog.updatedAt !== blog.createdAt && (
          <Typography variant='caption' color='text.secondary'>
            (updated {new Date(blog.updatedAt).toLocaleDateString()})
          </Typography>
        )}
      </Stack>

      {blog.tags.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
          {blog.tags.map(tag => (
            <Chip key={tag} label={`#${tag}`} size='small' variant='outlined' />
          ))}
        </Box>
      )}

      <Divider sx={{ mb: 3 }} />

      <MarkdownView>{blog.content}</MarkdownView>
    </Box>
  );
}
